import * as React from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { motion, useMotionValue, useSpring } from "framer-motion";
import {
  Calculator,
  DraftingCompass,
  Microscope,
  Stethoscope,
  Languages,
  GraduationCap,
  PencilRuler,
  NotebookTabs,
  LucideIcon } from
"lucide-react";

// Move animated texts outside component to prevent re-creation
const animatedTexts = [
"Medical Students",
"Undergraduate Students",
"International Students",
"Law Students",
"Graduate Students"];


interface FloatingIconProps {
  id: number;
  Icon: LucideIcon;
  className: string;
}

// A single floating icon component with motion logic
const FloatingIcon = ({
  mouseX,
  mouseY,
  iconData,
  index





}: {mouseX: React.MutableRefObject<number>;mouseY: React.MutableRefObject<number>;iconData: FloatingIconProps;index: number;}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, {
    stiffness: 300,
    damping: 20
  });
  const springY = useSpring(y, {
    stiffness: 300,
    damping: 20
  });
  React.useEffect(() => {
    const handleMouseMove = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const distance = Math.sqrt(
          Math.pow(mouseX.current - (rect.left + rect.width / 2), 2) +
          Math.pow(mouseY.current - (rect.top + rect.height / 2), 2)
        );
        if (distance < 150) {
          const angle = Math.atan2(
            mouseY.current - (rect.top + rect.height / 2),
            mouseX.current - (rect.left + rect.width / 2)
          );
          const force = (1 - distance / 150) * 50;
          x.set(-Math.cos(angle) * force);
          y.set(-Math.sin(angle) * force);
        } else {
          x.set(0);
          y.set(0);
        }
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [x, y, mouseX, mouseY]);
  return (
    <motion.div
      ref={ref}
      key={iconData.id}
      style={{
        x: springX,
        y: springY
      }}
      initial={{
        opacity: 0,
        scale: 0.5
      }}
      animate={{
        opacity: 1,
        scale: 1
      }}
      transition={{
        delay: index * 0.08,
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }}
      className={cn("absolute", iconData.className)}>

      <motion.div
        className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 p-3 rounded-3xl shadow-xl bg-card/80 backdrop-blur-md border border-border/10"
        animate={{
          y: [0, -8, 0, 8, 0],
          x: [0, 6, 0, -6, 0],
          rotate: [0, 5, 0, -5, 0]
        }}
        transition={{
          duration: 5 + Math.random() * 5,
          repeat: Infinity,
          repeatType: "mirror",
          ease: "easeInOut"
        }}>

        <iconData.Icon className="w-8 h-8 md:w-10 md:h-10 text-primary" />
      </motion.div>
    </motion.div>);

};
export const Hero = () => {
  const navigate = useNavigate();
  const [textIndex, setTextIndex] = React.useState(0);
  const [displayText, setDisplayText] = React.useState("");
  const [isDeleting, setIsDeleting] = React.useState(false);
  const mouseX = React.useRef(0);
  const mouseY = React.useRef(0);
  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    mouseX.current = event.clientX;
    mouseY.current = event.clientY;
  };

  // Define floating icon positions using Lucide icons
  const floatingIcons: FloatingIconProps[] = [
  {
    id: 1,
    Icon: Calculator,
    className: "top-[8%] left-[5%]"
  },
  {
    id: 2,
    Icon: DraftingCompass,
    className: "top-[12%] right-[8%]"
  },
  {
    id: 3,
    Icon: Microscope,
    className: "top-[48%] left-[2%]"
  },
  {
    id: 4,
    Icon: Stethoscope,
    className: "top-[55%] right-[4%]"
  },
  {
    id: 5,
    Icon: Languages,
    className: "bottom-[15%] left-[6%]"
  },
  {
    id: 6,
    Icon: GraduationCap,
    className: "bottom-[20%] right-[10%]"
  },
  {
    id: 7,
    Icon: PencilRuler,
    className: "top-[28%] left-[12%]"
  }];


  const avatars = [
  {
    src: "/images/avatars/student-1.jpg",
    alt: "Student 1",
    fallback: "S1"
  },
  {
    src: "/images/avatars/student-2.jpg",
    alt: "Student 2",
    fallback: "S2"
  },
  {
    src: "/images/avatars/student-3.jpg",
    alt: "Student 3",
    fallback: "S3"
  },
  {
    src: "/images/avatars/student-4.jpg",
    alt: "Student 4",
    fallback: "S4"
  }];


  React.useEffect(() => {
    const fullText = animatedTexts[textIndex];
    const handleTyping = () => {
      if (isDeleting) {
        setDisplayText((prev) => prev.substring(0, prev.length - 1));
      } else {
        setDisplayText((prev) => fullText.substring(0, prev.length + 1));
      }
    };
    const typingSpeed = isDeleting ? 75 : 150;
    const typeInterval = setInterval(handleTyping, typingSpeed);
    if (!isDeleting && displayText === fullText) {
      setTimeout(() => setIsDeleting(true), 2000);
    } else if (isDeleting && displayText === "") {
      setIsDeleting(false);
      setTextIndex((prev) => (prev + 1) % animatedTexts.length);
    }
    return () => clearInterval(typeInterval);
  }, [displayText, isDeleting, textIndex]);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({
      behavior: "smooth"
    });
  };
  return (
    <section
      onMouseMove={handleMouseMove}
      className="relative container mx-auto flex flex-col items-center justify-center text-center py-20 md:py-32 min-h-[700px] overflow-hidden">

      {/* Floating Icons Background */}
      <div className="absolute inset-0 w-full h-full pointer-events-none opacity-0 md:opacity-100 transition-opacity duration-500">
        {floatingIcons.map((iconData, index) =>
        <FloatingIcon key={iconData.id} mouseX={mouseX} mouseY={mouseY} iconData={iconData} index={index} />
        )}
      </div>

      {/* Content */}
      <div className="relative z-10">
        <div className="max-w-4xl">
          {/* Info Badge */}
          <div className="inline-flex items-center rounded-lg bg-secondary text-secondary-foreground px-3 py-1 text-sm font-medium mb-6">
            Loved by 5,000+ Students — and Growing Every Day
          </div>

          <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-7xl">
            <span className="block">The AI Tutor Built for  </span>
            <span className="relative mt-2 block w-fit mx-auto">
              <span className="absolute inset-0 -z-10 -m-2">
                <span className="absolute inset-0 border-2 border-dashed border-primary rounded-2xl"></span>
              </span>
              {/* Animated Text */}
              <span className="text-primary min-h-[1.2em] inline-flex items-center gap-3">
                <span>
                  {displayText}
                  <span className="animate-pulse">|</span>
                </span>
              </span>
            </span>
          </h1>

          <p className="mt-6 text-lg leading-8 text-muted-foreground max-w-2xl mx-auto">
            Upload anything. Get instant explanations, quizzes, and a study plan that fits your goals — not a generic
            template.
          </p>
        </div>

        <div className="mt-10 flex flex-col items-center gap-6">
          <div className="flex flex-wrap gap-4 justify-center">
            <Button
              size="lg"
              className="group relative px-8 py-6 text-lg overflow-hidden"
              onClick={() => navigate("/auth/signup")}>

              <span className="relative z-10">Start Learning Free →</span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
            </Button>
          </div>

          {/* Social Proof */}
          <div className="flex items-center gap-4">
            <div className="flex -space-x-2">
              {avatars.map((avatar, index) =>
              <Avatar key={index} className="border-2 border-background w-10 h-10">
                  <AvatarImage src={avatar.src} alt={avatar.alt} />
                  <AvatarFallback>{avatar.fallback}</AvatarFallback>
                </Avatar>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Join 5,000+ students who stopped struggling and started scoring.
            </p>
          </div>
        </div>
      </div>
    </section>);

};